<template>
  <template v-if="$route.name === 'AssignGroup'">
    <div class="col-12">
      <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
          <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
                {{ $t('groups.breadcrumbs.addGroup') }}
              </h6>
            </div>
          </div>
        </div>
       
        <div class="card-body px-0 pb-2 mx-2">
          <div class="row justify-content-center">
            <div class="col-md-6">
              <form @submit.prevent="handleSubmit">
                <div class="mb-3">
                  <label for="group-select" class="bmd-label-float">
                    {{ $t('assignGroup.selectGroupLabel') }}
                  </label>
                  <select 
                    id="group-select" 
                    class="form-select borderless-input"
                    v-model="selectedGroupId"
                    :disabled="isLoading || unsignedGroups.length === 0"
                  >
                    <option value="" disabled>
                      {{ $t('assignGroup.selectGroupPlaceholder') }}
                    </option>
                    <option 
                      v-for="group in unsignedGroups" 
                      :key="group.id" 
                      :value="group.id"
                    >
                      {{ group.name }}
                    </option>
                    <option v-if="unsignedGroups.length === 0" disabled>
                      {{ $t('assignGroup.noAvailableGroups') }}
                    </option>
                  </select>
                </div>
  
                <!-- Action Buttons -->
                <div class="d-flex gap-3 mt-3">
                  <button 
                    type="submit" 
                    class="btn btn-primary btn-sm flex-grow-1"
                    :disabled="isLoading || !selectedGroupId"
                  >
                    <span v-if="isLoading">
                      <i class="fas fa-spinner fa-spin me-2"></i>
                      {{ $t('assignGroup.adding') }}
                    </span>
                    <span v-else>
                      <i class="fas fa-plus" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                      {{ $t('assignGroup.addGroup') }}
                    </span>
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-outline-secondary btn-sm flex-grow-1"
                    @click="handleCancel"
                    :disabled="isLoading"
                  >
                    <i class="fas fa-times" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                    {{ $t('baseDialog.cancel') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref, onMounted, defineProps } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();
const toast = useToast();
const router = useRouter();
const store = useStore();

const props = defineProps({
  classId: {
    type: [String, Number],
    required: true
  },
  className: {
    type: String,
    required: true
  }
});

const unsignedGroups = ref([]);
const selectedGroupId = ref(null);
const isLoading = ref(false);

onMounted(async () => {
  await fetchUnsignedGroups();
});

const fetchUnsignedGroups = async () => {
  isLoading.value = true;
  try {
    const response = await store.dispatch('groups/fetchUnsignedGroups', props.classId);
    unsignedGroups.value = response.data || [];
    
    if (unsignedGroups.value.length === 0) {
      toast.warning(t('assignGroup.noAvailableGroups'));
    }
  } catch (error) {
    console.error('Error fetching unsigned groups:', error);
    toast.error(error.response?.data?.message || t('assignGroup.loadError'));
  } finally {
    isLoading.value = false;
  }
};

const handleSubmit = async () => {
  if (!selectedGroupId.value) return;
  
  isLoading.value = true;
  
  try {
    await store.dispatch('groups/assignGroupToClass', {
      classId: props.classId,
      group_id: selectedGroupId.value,
    });
    
    toast.success(t('assignGroup.addSuccess'));
    router.push(`/classes/${props.classId}/groups`);
  } catch (error) {
    console.error('Error adding group:', error);
    toast.error(error.response?.data?.message || t('assignGroup.addError'));
  } finally {
    isLoading.value = false;
  }
};

const handleCancel = () => {
  router.push(`/classes/${props.classId}/groups`);
};
</script>